import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CalendarCheck, CheckCircle2, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Section, Field, RetryImage } from "./ui";
import { SiteVisitSlots } from "./SiteVisitSlots";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const longDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

// Autumn no-booking-fee flow: replaces "pay £499" with a free 1-hour site visit.
export function SiteVisitStep({ config, site, route, planningRef, postcode, quoteResult, settings, planningQuoteId, planningContact, pdQuote, contact, onBack }) {
  const isPlanning = route === "planning";
  const [details, setDetails] = useState({ name: planningContact?.name || contact?.name || "", email: planningContact?.email || contact?.email || "", phone: planningContact?.mobile || contact?.phone || "", address: "", comments: "" });
  const [slot, setSlot] = useState({ date: "", time: "" });
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const total = Math.round(quoteResult?.total_inc_vat || pdQuote?.total_inc_vat || 0);
  const depositPct = settings?.deposit_pct ?? 0.10;
  const deposit = Math.max(Math.round(total * depositPct), settings?.booking_fee ?? 499);
  const until = settings?.site_visit_mode_until ? new Date(settings.site_visit_mode_until).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "";

  const submit = async () => {
    if (!details.name || !details.email || !details.phone.trim()) { toast.error("Name, email and phone are required."); return; }
    if (!details.address.trim()) { toast.error("Please enter the site address so we know where to visit."); return; }
    if (!slot.date || !slot.time) { toast.error("Pick a day and a time for your visit."); return; }
    if (!agree) { toast.error("Please confirm you're happy for us to visit at that time."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/site-visit/book", {
        ...details, postcode, route, planning_ref: planningRef,
        quote: isPlanning ? { route: "planning" } : { ...config, site, route },
        quote_id: isPlanning ? planningQuoteId : (pdQuote?.quote_id || undefined),
        date: slot.date, time: slot.time,
      });
      setDone(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book your site visit");
    } finally { setSubmitting(false); }
  };

  if (done) return <SiteVisitConfirmed result={done} email={details.email} />;

  return (
    <Section title="Book your free site visit" eyebrow="Final step · Nothing to pay today">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="border border-[#C79A3B] bg-[#FBF7EF] p-4 mb-6 text-sm flex gap-3" data-testid="sv-offer-note">
            <Gift size={18} className="text-[#C79A3B] shrink-0 mt-0.5" />
            <div>
              <b>Nothing to pay to book{until ? ` — no booking fee until ${until}` : ""}.</b>
              <div className="text-[#6B7280] text-xs mt-1 leading-relaxed">Book a free 1-hour visit — we&rsquo;ll check access, measure up and talk through your design and quote. Your {Math.round(depositPct * 100)}% deposit{total ? ` (£${deposit.toLocaleString("en-GB")})` : ""} is only paid <b className="text-[#12263A]">after the visit</b>, and only if you decide to go ahead. Nothing is taken when you book.</div>
            </div>
          </div>
          <Field label="Full name" v={details.name} on={v=>setDetails({...details,name:v})} testid="sv-name"/>
          <Field label="Email" type="email" v={details.email} on={v=>setDetails({...details,email:v})} testid="sv-email"/>
          <Field label="Phone *" v={details.phone} on={v=>setDetails({...details,phone:v})} testid="sv-phone"/>
          <Field label="Site address *" v={details.address} on={v=>setDetails({...details,address:v})} testid="sv-address"/>
          <div>
            <label className="label-eyebrow block mb-2 mt-4">Anything we should know before we visit?</label>
            <textarea data-testid="sv-comments" rows={3} value={details.comments} onChange={e=>setDetails({...details,comments:e.target.value})} placeholder="Parking, access, pets, best way to reach you…" className="w-full border border-[#E5E7EB] bg-white px-4 py-3"/>
          </div>
        </div>
        <div>
          {!isPlanning && pdQuote && (
            <div className="border border-[#E5E7EB] bg-white p-4 mb-6" data-testid="sv-quote-ref">
              <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
                <div className="flex gap-3 shrink-0">
                  <img src={`${BACKEND}${pdQuote.photo_url}`} alt="Your extension exterior" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} className="w-24 h-20 object-cover border border-[#E5E7EB] bg-[#F4F4F4]" />
                  {pdQuote.interior_photo_url && <RetryImage src={`${BACKEND}${pdQuote.interior_photo_url}`} alt="Your extension interior" className="w-24 h-20 object-cover border border-[#E5E7EB] bg-[#F4F4F4]" testid="sv-quote-interior" />}
                </div>
                <div className="text-sm min-w-0">
                  <div className="label-eyebrow">Your quote</div>
                  <div className="text-xl font-black text-[#C79A3B] break-all">{pdQuote.reference} <span className="text-[#12263A]">· £{total.toLocaleString("en-GB")}</span></div>
                  <div className="text-xs text-[#6B7280] mt-1">Fixed price incl. VAT, valid until <b>{new Date(pdQuote.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</b>.</div>
                </div>
              </div>
            </div>
          )}
          <SiteVisitSlots date={slot.date} time={slot.time} onChange={(date, time) => setSlot({ date, time })} />

          <div className="bg-[#12263A] text-white p-6 mt-6" data-testid="sv-summary">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Due now</div>
            <div className="text-3xl font-black mt-1" data-testid="sv-due-now">£0</div>
            <div className="text-xs text-white/70 mt-2">
              {slot.date && slot.time
                ? <>Your free visit: <b className="text-white">{longDate(slot.date)} at {slot.time}</b> (about 1 hour).</>
                : "Pick a day and time above. Nothing is paid when you book."}
            </div>
            <div className="mt-4 pt-3 border-t border-white/15 flex items-start justify-between gap-3 text-xs" data-testid="sv-deposit-after">
              <div>
                <div className="uppercase tracking-widest text-[#C79A3B]">Deposit ({Math.round(depositPct * 100)}%)</div>
                <div className="text-white/70 mt-0.5">Paid <b className="text-white">after your visit</b> — only if you decide to go ahead.</div>
              </div>
              <div className="text-lg font-black shrink-0">{total ? `£${deposit.toLocaleString("en-GB")}` : "10%"}</div>
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs mt-6 border border-[#E5E7EB] bg-white p-4">
            <input data-testid="sv-agree" type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 accent-[#C79A3B]"/>
            <span>I&rsquo;m happy for The Extension Works to visit the address above at the chosen time. This is free and doesn&rsquo;t commit me to anything — nothing is paid today, and a deposit is only taken after the visit if I choose to go ahead.</span>
          </label>

          <div className="mt-6 flex gap-3">
            <button data-testid="sv-back" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16}/> Back</button>
            <button data-testid="sv-submit" disabled={submitting} className="de-btn-primary" onClick={submit}>{submitting ? "Booking…" : "Book my free site visit"} <ArrowRight size={16}/></button>
          </div>
        </div>
      </div>
    </Section>
  );
}

function SiteVisitConfirmed({ result, email }) {
  return (
    <Section title="Your site visit is booked" eyebrow="All done — nothing paid today">
      <div className="max-w-2xl" data-testid="sv-confirmed">
        <div className="border border-[#1F6B3B] bg-[#D8EED8] p-6 flex gap-4">
          <CheckCircle2 size={28} className="text-[#1F6B3B] shrink-0" />
          <div>
            <div className="text-xl font-black text-[#12263A]" data-testid="sv-confirmed-when">{result.date_label} at {result.time}</div>
            <div className="text-sm text-[#12263A]/80 mt-1">Reference <b data-testid="sv-confirmed-ref">{result.reference}</b> · about 1 hour · free, no obligation</div>
            <p className="text-sm text-[#12263A]/80 mt-3 leading-relaxed">We&rsquo;ve emailed the details to <b>{email}</b>. One of our team will check access, measure up and talk through your design and quote.</p>
            <p className="text-sm text-[#12263A] mt-2 leading-relaxed font-semibold" data-testid="sv-confirmed-deposit">Nothing has been paid today. Your deposit is only due <u>after the visit</u> — and only if you decide to go ahead. We&rsquo;ll agree your start date at the same time.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/portal" data-testid="sv-confirmed-portal" className="de-btn-primary"><CalendarCheck size={16} /> Open my client portal</Link>
          <Link to="/" data-testid="sv-confirmed-home" className="de-btn-secondary">Back to home</Link>
        </div>
        <p className="text-xs text-[#6B7280] mt-4">Need to change the time? Reply to the confirmation email or call us and we&rsquo;ll find another slot.</p>
      </div>
    </Section>
  );
}
